import React, { useMemo } from "react";
// MUI
import Skeleton from "@mui/material/Skeleton";
import MuiButton from "@mui/material/Button";
import makeStyles from "@mui/styles/makeStyles";

// Types
type ChipListItemProps = {
  className?: string;
  items: ItemType[];
  selected?: null | string | number | boolean | (number | string)[];
  minWidth?: number;
  width?: number | string;
  highlight?: boolean;
  loading?: boolean;
  // callback
  onSelect?: (selected: ItemType) => any;
};

type ItemType = {
  id: number | string | boolean;
  value: number | string | number[];
};

// Styles
const COLORS = {
  primary: "var(--primary-theme-color)",
  bg: "#ECF4FF",
  opacity: "rgba(133,133,133,0.03)",
  grey: "rgba(57, 57, 57, 1)",
};

const boxChipStyles = {
  gap: "11px",
  display: "grid",
  "& > div": {
    width: "100%",
    position: "relative",
  },
  "& button": {
    border: `1px solid rgba(217, 217, 217, 1)`,
    borderRadius: "8px",
    padding: ".4375rem 1rem",
    display: "flex",
    alignItems: "center",
    fontFamily: "inherit",
    justifyContent: "center",
    width: "100%",
    fontSize: ".9375rem",
    color: COLORS.grey,
    "& img": {
      width: "20px",
    },
    "& .MuiTouchRipple-ripple": {
      color: "var(--primary-theme-color)",
    },
  },
  "& .active": {
    "& button": {
      border: `1px solid ${COLORS.primary}`,
      color: COLORS.primary,
      fontWeight: "bold",
    },
  },
};

const useStyles = makeStyles((theme) => ({
  box_chip: boxChipStyles,
  highlight: {
    "& .active": {
      "& button": {
        backgroundColor: "var(--oa-bg-color-light)",
      },
    },
  },
}));

const ChipListItem = (props: ChipListItemProps) => {
  const classes = useStyles();

  const items = useMemo(() => {
    const skeletons = Array(8)
      .fill("")
      .map((_, index) => ({ id: index + 1 } as any));

    return props.loading ? skeletons : props.items;
  }, [props.loading, props.items]);

  // Handler
  const handleSelect = (selected: ItemType) => () => {
    // if (props.selected !== selected.id) {
    props.onSelect?.(selected);
    // }
  };

  // Container styles with optional width
  const containerStyle = {
    gridTemplateColumns: props.minWidth
      ? `repeat(auto-fill, minmax(${props.minWidth}px, 1fr))`
      : `repeat(auto-fill, minmax(${props.width}px, ${props.width}px))`,
  };

  return (
    <div
      className={`${classes.box_chip} ${props.className || ""} ${
        props.highlight ? classes.highlight : ""
      }`}
      style={containerStyle}
    >
      {items.map((item, index, self) => (
        <RenderContent
          key={`content-${item.id}`}
          id={`chip-item-${item.id}`}
          active={
            Array.isArray(props.selected)
              ? props.selected.includes(item.id)
              : item.id === props.selected
          }
          value={item.value}
          zIndex={self.length - index}
          loading={props.loading}
          // callback
          onClick={handleSelect(item)}
        />
      ))}
    </div>
  );
};

/* ====================================================== */
/*                     RenderContent;                     */
/* ====================================================== */
type RenderContentProps = {
  id?: string;
  active: boolean;
  value: ItemType["value"];
  zIndex: number;
  loading?: boolean;
  // callback
  onClick: () => any;
};

const RenderContent = (props: RenderContentProps) => {
  const valueContent = useMemo(() => {
    return Array.isArray(props.value)
      ? `${props.value[0]} - ${props.value[1]}`
      : props.value;
  }, [props.value]);

  return (
    <div
      className={props.active ? "active" : ""}
      style={{ zIndex: props.zIndex }}
    >
      <MuiButton
        id={props.id}
        style={
          props.loading
            ? { background: COLORS.opacity, border: 0, boxShadow: "none" }
            : {}
        }
        variant="text"
        onClick={props.onClick}
      >
        {props.loading ? (
          <Skeleton animation="pulse" width={"100%"} height={18} />
        ) : (
          valueContent
        )}
      </MuiButton>
    </div>
  );
};

export default React.memo(ChipListItem);
